import useTranslation from '@app/hooks/use-translation';
import {
  useCreateScoreTemplateMutation,
  useDeleteScoreTemplateMutation,
  useGetScoreTemplatesListQuery,
  useLazyGetSingleScoreTemplateQuery,
} from '@app/store/api/qm-module/qm-score-templates/qm-score-templates.api';
import { Button, CircleButton, EasyModal, PageHeader } from '@ui';
import React, { useState } from 'react';

import { SingleScaleTemplateObjectAndCreateType } from '@app/interfaces/qm-module/qm-score-templates/qm-score-templates.type';

import RubberTable from '@app/components/rubber-table';

import { TableColumn } from '@app/components/rubber-table/table.types';

import { RemovalModal } from '@app/components/removal-modal';

import TemplateSettingForm from './blocks/template-setting-form';

const QmManageAssessmentTemplates = () => {
  const { t } = useTranslation('pages.qmScoreTemplates');
  // eslint-disable-next-line
  const { data: templatesList, isLoading: templatesListLoading } = useGetScoreTemplatesListQuery();
  const [deleteTemplate] = useDeleteScoreTemplateMutation();
  const [createTemplate] = useCreateScoreTemplateMutation();
  const [getTemplateData] = useLazyGetSingleScoreTemplateQuery();
  const [templateIdForDelete, setTemplateIdForDelete] = useState<string>();
  const [createTemplateModalState, changeCreateTemplateModalState] = useState(false);
  const [templateValues, setTemplateValue] = useState<
    SingleScaleTemplateObjectAndCreateType & { rs_template_id: string }
  >();

  function onCloseModal() {
    changeCreateTemplateModalState(false);
  }

  function onOpenTemplateSettings(rs_template_id: string) {
    getTemplateData({ rs_template_id })
      .unwrap()
      .then((data) => {
        setTemplateValue(data);
        changeCreateTemplateModalState(true);
      });
  }

  function onCloseDeleteModal() {
    setTemplateIdForDelete(undefined);
  }

  function approveDeleteTemplate() {
    if (!templateIdForDelete) return;
    deleteTemplate({ rs_template_id: templateIdForDelete });
  }

  function onOpenDeleteModal(id: string) {
    setTemplateIdForDelete(id);
  }

  function onCopyTemplate(rs_template_id: string) {
    getTemplateData({ rs_template_id })
      .unwrap()
      .then((data) => {
        createTemplate({
          ...data,
          rating_scale: { ...data.rating_scale, name: data.rating_scale.name + ' копия' },
        });
      });
  }

  const tableCols: TableColumn[] = [
    { index: 'name', title: t('table.name') },
    { index: 'dt_add', title: t('table.dt_add') },
    { index: 'dt_update', title: t('table.dt_update') },
    { index: 'actions', title: t('table.actions'), maxWidth: 130, divider: 'left' },
  ];
  const tableRows = (templatesList || []).map((template) => ({
    ...template,
    actions: (
      <div className="flex items-center gap-[5px]">
        <CircleButton
          onClick={() => onOpenTemplateSettings(template.rs_template_id)}
          className="text-3color"
          icon="SettingIcon"
          size={18}
          hintTitle={t('hints.setting')}
        />
        <CircleButton
          onClick={() => onCopyTemplate(template.rs_template_id)}
          className="text-action"
          icon="CopyIcon"
          size={18}
          hintTitle={t('hints.copy')}
        />
        <CircleButton
          onClick={() => onOpenDeleteModal(template.rs_template_id)}
          className="text-basic_red"
          icon="TrashIcon"
          size={18}
          hintTitle={t('hints.delete')}
        />
      </div>
    ),
  }));
  return (
    <>
      <PageHeader label={t('page_title')}>
        <div className="self-end">
          <Button
            onClick={() => changeCreateTemplateModalState(true)}
            label={t('create_button_label')}
          />
        </div>
      </PageHeader>
      <div className="flex flex-col gap-[20px] w-full h-full py-[22px]">
        <div>
          <RubberTable dataSource={tableRows} columns={tableCols} />
        </div>
      </div>
      <EasyModal
        variant="large"
        label={t('template_settings')}
        withoutFooter
        onClose={onCloseModal}
        show={createTemplateModalState}
      >
        {createTemplateModalState && (
          <TemplateSettingForm templateValues={templateValues} onClose={onCloseModal} />
        )}
      </EasyModal>
      <RemovalModal
        show={!!templateIdForDelete}
        onClose={onCloseDeleteModal}
        description={t('remove_modal_desc')}
        handleDelete={approveDeleteTemplate}
      />
    </>
  );
};

export default QmManageAssessmentTemplates;
